export const DEFAULT_SHEET = {
  shifts: [
    { name: "Early", start: "06:00", end: "14:00" },
    { name: "Late", start: "14:00", end: "22:00" },
    { name: "Night", start: "22:00", end: "06:00" },
  ],
  gapMin: 15,
  breakMin: 30,
  otMin: 60,
};
const DAY = 86400;
export const GAP_OPTIONS = [5, 10, 15, 30, 60];

const hm = (s) => { const [h, m] = s.split(":").map(Number); return h * 3600 + m * 60; };
const overlap = (a, b, c, d) => Math.max(0, Math.min(b, d) - Math.max(a, c));

export const fmtClock = (sec) => `${String(Math.floor(sec / 3600)).padStart(2, "0")}:${String(Math.floor((sec % 3600) / 60)).padStart(2, "0")}`;
export const fmtDur = (sec) => {
  const m = Math.round((sec || 0) / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};
export const fmtHours = (sec) => ((sec || 0) / 3600).toFixed(1);

export function weekStart(dk) {
  const [y, m, d] = dk.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(d - ((dt.getUTCDay() + 6) % 7));
  return dt.toISOString().slice(0, 10);
}

export const dayLabel = (dk) => new Date(`${dk}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", timeZone: "UTC" });

function shiftWindow(f, sh) {
  let ss = hm(sh.start), se = hm(sh.end);
  if (se <= ss) se += DAY;
  if (se > DAY && f < DAY / 2) { ss -= DAY; se -= DAY; }
  return [ss, se];
}

function detectShift(f, shifts) {
  let best = shifts[0], bd = Infinity;
  for (const sh of shifts) {
    const d = Math.abs(f - hm(sh.start));
    const dist = Math.min(d, DAY - d);
    if (dist < bd) { bd = dist; best = sh; }
  }
  return best;
}

function dayStats(day, [p, q, f, l, gaps], c, override) {
  const sh = override || detectShift(f, c.shifts);
  const [ss, se] = shiftWindow(f, sh);
  const shiftLen = se - ss;
  const late = Math.min(shiftLen, Math.max(0, f - ss));
  const early = Math.min(shiftLen, Math.max(0, se - l));
  const big = gaps.filter(([, dur]) => dur >= c.gapSec).map(([start, dur]) => ({ start, dur }));
  const gapTime = big.reduce((s, g) => s + overlap(g.start, g.start + g.dur, ss, se), 0);
  const gapLost = Math.max(0, gapTime - c.breakSec);
  const worked = Math.max(0, l - f);
  const otRaw = Math.max(0, ss - f) + Math.max(0, l - se);
  const ot = otRaw >= c.otSec ? otRaw : 0;
  return { day, shift: sh.name, shiftStart: ss, shiftEnd: se, picks: p, qty: q, first: f, last: l, worked, late, early, gaps: big, gapTime, gapLost, lost: Math.min(shiftLen, late + early + gapLost), ot, otDays: ot ? 1 : 0, pph: worked > 0 ? p / (worked / 3600) : 0 };
}

export function monthlyWorked(userDays) {
  const out = {};
  for (const [k, d] of Object.entries(userDays || {})) out[k.slice(0, 7)] = (out[k.slice(0, 7)] || 0) + Math.max(0, d[3] - d[2]);
  return out;
}

const mode = (arr) => {
  const m = {};
  arr.forEach((x) => { m[x] = (m[x] || 0) + 1; });
  return Object.entries(m).sort((a, b) => b[1] - a[1])[0]?.[0];
};

const sumKeys = ["picks", "qty", "worked", "late", "early", "gapTime", "gapLost", "lost", "ot", "otDays"];

export function computeSheet(days, from, to, cfg) {
  const c = { shifts: cfg.shifts, gapSec: cfg.gapMin * 60, breakSec: cfg.breakMin * 60, otSec: (cfg.otMin ?? 60) * 60 };
  const users = [];
  for (const [id, ud] of Object.entries(days || {})) {
    const res = shiftResolver(id, ud, cfg);
    const list = Object.keys(ud).filter((k) => k.slice(0, 7) >= from && k.slice(0, 7) <= to).sort().map((k) => dayStats(k, ud[k], c, res(k)));
    if (!list.length) continue;
    const wk = new Map();
    for (const d of list) {
      const w = weekStart(d.day);
      const row = wk.get(w) || { week: w, days: [], gapCount: 0, ...Object.fromEntries(sumKeys.map((k) => [k, 0])) };
      row.days.push(d); row.gapCount += d.gaps.length;
      sumKeys.forEach((k) => { row[k] += d[k]; });
      wk.set(w, row);
    }
    const weeks = [...wk.values()].map((w) => ({ ...w, pph: w.worked ? w.picks / (w.worked / 3600) : 0 }));
    const tot = Object.fromEntries(sumKeys.map((k) => [k, list.reduce((s, d) => s + d[k], 0)]));
    users.push({
      id, days: list, weeks, ...tot,
      daysWorked: list.length,
      mainShift: mode(list.map((d) => d.shift)),
      gapCount: list.reduce((s, d) => s + d.gaps.length, 0),
      avgPicksWk: tot.picks / weeks.length,
      avgHoursWk: tot.worked / 3600 / weeks.length,
      avgLostDay: tot.lost / list.length,
      avgStart: list.reduce((s, d) => s + d.first, 0) / list.length,
      avgFinish: list.reduce((s, d) => s + d.last, 0) / list.length,
      pph: tot.worked ? tot.picks / (tot.worked / 3600) : 0,
    });
  }
  return users.sort((a, b) => b.picks - a.picks);
}

export const weeksOf = (users) => [...new Set(users.flatMap((u) => u.weeks.map((w) => w.week)))].sort();

export function leaderboard(users, week) {
  return users
    .map((u) => ({ u, w: u.weeks.find((x) => x.week === week) }))
    .filter((x) => x.w)
    .map(({ u, w }) => ({ id: u.id, shift: mode(w.days.map((d) => d.shift)), ...w, lostPerDay: w.lost / w.days.length }))
    .sort((a, b) => b.lost - a.lost);
}

export function gapHeatmap(users) {
  const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
  const counts = Array.from({ length: 7 }, () => new Array(24).fill(0));
  for (const u of users) for (const d of u.days) {
    const wd = (new Date(`${d.day}T00:00:00Z`).getUTCDay() + 6) % 7;
    for (const g of d.gaps) {
      counts[wd][Math.floor(g.start / 3600) % 24]++;
      let t = g.start;
      const end = Math.min(g.start + g.dur, DAY);
      while (t < end) {
        const h = Math.floor(t / 3600), next = Math.min(end, (h + 1) * 3600);
        grid[wd][h] += (next - t) / 60;
        t = next;
      }
    }
  }
  return { grid, counts };
}

export function sheetCsv(user) {
  const head = ["Week commencing", "Date", "Shift", "Picks", "Qty", "First pick", "Last pick", "Hours worked", "Picks/hr", "Late start (min)", "Early finish (min)", "Large gaps", "Gap time (min)", "Lost time (min)", "Overtime (min)"];
  const m = (s) => Math.round(s / 60);
  const lines = [head.join(",")];
  for (const w of user.weeks) {
    for (const d of w.days) lines.push([w.week, d.day, d.shift, d.picks, d.qty, fmtClock(d.first), fmtClock(d.last), fmtHours(d.worked), d.pph.toFixed(1), m(d.late), m(d.early), d.gaps.length, m(d.gapTime), m(d.lost), m(d.ot)].join(","));
    lines.push([w.week, "WEEK TOTAL", "", w.picks, w.qty, "", "", fmtHours(w.worked), w.pph.toFixed(1), m(w.late), m(w.early), w.gapCount, m(w.gapTime), m(w.lost), m(w.ot)].join(","));
  }
  return lines.join("\n");
}

export function userMonthly(u, userDays, months, cfg) {
  const sheet = userDays ? computeSheet({ [u.id]: userDays }, months[0], months[months.length - 1], cfg)[0] : null;
  const agg = {};
  sheet?.days.forEach((d) => {
    const a = agg[d.day.slice(0, 7)] || (agg[d.day.slice(0, 7)] = { worked: 0, lost: 0, days: 0 });
    a.worked += d.worked; a.lost += d.lost; a.days++;
  });
  const keys = Object.keys(userDays || {}).sort();
  const absent = absentWeekdays(userDays || {}, keys[0], keys[keys.length - 1], cfg.holidays);
  return months.map((m) => {
    const a = agg[m] || { worked: 0, lost: 0, days: 0 };
    return derive({ key: m, visits: u.monthly[m]?.[0] || 0, qty: u.monthly[m]?.[1] || 0, ...a, absent: absent[m] || 0, months: 1 });
  });
}

function absentWeekdays(userDays, first, last, holidays) {
  const out = {};
  if (!first) return out;
  const d = new Date(`${first}T00:00:00Z`), end = new Date(`${last}T00:00:00Z`);
  for (; d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const wd = d.getUTCDay(), k = d.toISOString().slice(0, 10);
    if (wd !== 0 && wd !== 6 && !userDays[k] && !holidays?.has(k)) out[k.slice(0, 7)] = (out[k.slice(0, 7)] || 0) + 1;
  }
  return out;
}

export function derive(r) {
  const hrs = r.worked / 3600;
  return { ...r, hrs, vph: hrs ? r.visits / hrs : null, qph: hrs ? r.qty / hrs : null, lostDay: r.days ? r.lost / r.days : null };
}

export function toQuarters(rows) {
  const out = new Map();
  for (const r of rows) {
    const k = `${r.key.slice(0, 4)} Q${Math.ceil(+r.key.slice(5, 7) / 3)}`;
    const q = out.get(k) || { key: k, visits: 0, qty: 0, worked: 0, lost: 0, days: 0, absent: 0, months: 0 };
    ["visits", "qty", "worked", "lost", "days", "absent"].forEach((f) => { q[f] += r[f]; });
    q.months++;
    out.set(k, q);
  }
  return [...out.values()].map(derive);
}

export const pctChange = (cur, prev) => (cur == null || !prev ? null : ((cur - prev) / prev) * 100);

const round30 = (sec, up) => (up ? Math.ceil(sec / 1800) : Math.floor(sec / 1800)) * 1800;
const med = (a) => { const s = [...a].sort((x, y) => x - y); return s.length ? s[Math.floor(s.length / 2)] : null; };

export function suggestShift(userDays, shifts = DEFAULT_SHEET.shifts, minDays = 5) {
  const days = Object.values(userDays || {}).filter((d) => d[0] >= 5);
  if (days.length < minDays) return null;
  const f = med(days.map((d) => d[2])), l = med(days.map((d) => d[3]));
  const start = Math.round(f / 1800) * 1800, end = round30(l, true);
  const len = (end - start) / 3600;
  const near = (a, b) => { const d = Math.abs(a - b); return Math.min(d, DAY - d) <= 2700; };
  const std = len >= 6 ? shifts.find((s) => near(hm(s.start), start)) || shifts.find((s) => near(hm(s.end), end % DAY)) : null;
  return { start: fmtClock(start), end: fmtClock(end % DAY), medFirst: f, medLast: l, hours: len, standard: std || null, partTime: len < 6 };
}

export const normOverride = (v) => (!v ? [] : Array.isArray(v) ? [...v].sort((a, b) => a.since.localeCompare(b.since)) : [{ since: "0000-01-01", ...v }]);

const winToShift = (sg) => (!sg ? null : sg.standard ? { ...sg.standard, source: "auto" } : { name: `${sg.partTime ? "Part-time" : "Custom"} ${sg.start}–${sg.end}`, start: sg.start, end: sg.end, source: "auto" });

export function autoMonthly(ud, shifts) {
  const by = {};
  Object.entries(ud || {}).forEach(([k, d]) => { (by[k.slice(0, 7)] || (by[k.slice(0, 7)] = {}))[k] = d; });
  return Object.fromEntries(Object.entries(by).map(([m, days]) => [m, suggestShift(days, shifts, 8)]));
}

export function shiftResolver(id, ud, cfg) {
  const manual = normOverride(cfg.overrides?.[id]);
  const auto = cfg.autoShift === false ? {} : autoMonthly(ud, cfg.shifts);
  const overall = cfg.autoShift === false ? null : winToShift(suggestShift(ud, cfg.shifts));
  return (day) => {
    for (let i = manual.length - 1; i >= 0; i--) if (manual[i].since <= day) return { name: `Assigned ${manual[i].start}–${manual[i].end}`, start: manual[i].start, end: manual[i].end, source: "manual" };
    return winToShift(auto[day.slice(0, 7)]) || overall;
  };
}

export function autoPeriods(ud, shifts = DEFAULT_SHEET.shifts) {
  const out = [];
  Object.entries(autoMonthly(ud, shifts)).sort().forEach(([m, sg]) => {
    if (!sg) return;
    const w = winToShift(sg), last = out[out.length - 1];
    if (last && last.label === w.name.split(" ")[0] && Math.abs(hm(last.start) - hm(w.start)) <= 1800 && Math.abs(hm(last.end) - hm(w.end)) <= 1800) last.to = m;
    else out.push({ from: m, to: m, start: w.start, end: w.end, label: w.name.split(" ")[0] });
  });
  return out;
}

const winOf = (sg) => (sg ? (sg.standard ? { start: sg.standard.start, end: sg.standard.end, label: sg.standard.name } : { start: sg.start, end: sg.end, label: sg.partTime ? "Part-time" : "Custom" }) : null);
const diffH = (a, b) => Math.max(Math.abs(hm(a.start) - hm(b.start)), Math.abs(hm(a.end) - hm(b.end))) / 3600;

export function shiftReview(allDays, overrides = {}, recentDays = 28) {
  const out = [];
  for (const [id, ud] of Object.entries(allDays || {})) {
    const keys = Object.keys(ud).sort();
    if (!keys.length) continue;
    const cut = new Date(`${keys[keys.length - 1]}T00:00:00Z`);
    cut.setUTCDate(cut.getUTCDate() - recentDays);
    const c = cut.toISOString().slice(0, 10);
    const overall = winOf(suggestShift(ud));
    const recent = winOf(suggestShift(Object.fromEntries(keys.filter((k) => k > c).map((k) => [k, ud[k]]))));
    const man = normOverride(overrides[id]);
    const ov = man[man.length - 1];
    const current = ov ? { start: ov.start, end: ov.end, label: "Assigned" } : overall;
    if (!current) continue;
    const reasons = [];
    if (!ov && (current.label === "Part-time" || current.label === "Custom")) reasons.push("nonstandard");
    if (recent && diffH(recent, current) >= 1) reasons.push("changed");
    if (reasons.length) out.push({ id, current, overall, recent, reasons, since: c, manual: man });
  }
  return out.sort((a, b) => b.reasons.includes("changed") - a.reasons.includes("changed") || a.id.localeCompare(b.id));
}
